import {useMemo} from 'preact/hooks';

import {dateDiff} from 'utilities/dates';
import {Theme} from 'utilities/color';

import {PipelineProps, Scenario, ScenarioCost, ScenarioVariant} from '../types';
import {getDefaultScenario, formatFreeShippingThreshold} from '../helpers';

export function destinationAddress(
  currentScenario: Scenario,
  {deliveryEstimates}: PipelineProps,
) {
  return {
    ...currentScenario,
    destinationAddress: deliveryEstimates?.deliveryAddress,
  };
}

function titleLine(
  currentScenario: Scenario,
  {i18n, promiseProps}: PipelineProps,
) {
  const {translate, formatDate} = i18n;
  const {cost, variant, maxDeliveryDate, timezone} = promiseProps;
  const maxDeliveryDateShort = formatDate(maxDeliveryDate, timezone);

  return {
    ...currentScenario,
    title: translate(`title.${cost}.${variant}`, {
      maxDeliveryDateShort,
    }),
  };
}

function destinationLine(
  currentScenario: Scenario,
  {i18n, promiseProps}: PipelineProps,
) {
  const {translate, formatDate} = i18n;
  const {variant, maxDeliveryDate, cost, timezone} = promiseProps;
  const maxDeliveryDateShort = formatDate(maxDeliveryDate, timezone);

  switch (variant) {
    case ScenarioVariant.SameDay:
    case ScenarioVariant.OneDay:
    case ScenarioVariant.TwoDay:
    case ScenarioVariant.ThreeOrMoreDays:
    case ScenarioVariant.BeforeTheWeekend:
      return {
        ...currentScenario,
        locationPrefix: translate(`destination.${cost}.${variant}`, {
          maxDeliveryDateShort,
        }),
        destinationLineHasPrefix: true,
      };
  }
}

/*
// Disabled for now, until we resolve discrepancies between the backend and the frontend timezones
function fasterOptionAvailable(
  currentScenario: Scenario,
  {i18n, promiseProps}: PipelineProps,
) {
  const {translate} = i18n;
  const {hasFasterOptions} = promiseProps;

  if (hasFasterOptions) {
    return {
      ...currentScenario,
      fasterOptionsAvailableExtraLineMessage: translate(
        'title.extra.fasterOptionAvailable',
      ),
    } as Scenario;
  }

  return currentScenario;
}
*/

function promiseExpiryLine(
  currentScenario: Scenario,
  {deliveryEstimates, promiseProps}: PipelineProps,
) {
  const expiresAt =
    deliveryEstimates?.selectedShippingOption?.expiresAt ?? null;

  function parseExpiry(expiresAt: string | null) {
    if (expiresAt && promiseProps.daysToDelivery < 6) {
      const expiresAtDateDiff = dateDiff(new Date(), expiresAt, [
        'hour',
        'minute',
      ]);

      if (expiresAtDateDiff.hour === 0 && expiresAtDateDiff.minute === 0) {
        return null;
      }

      return expiresAtDateDiff;
    } else {
      return null;
    }
  }

  return {
    ...currentScenario,
    expiry: parseExpiry(expiresAt),
  };
}

function conditionalFreeLine(
  currentScenario: Scenario,
  {i18n, deliveryEstimates, promiseProps}: PipelineProps,
) {
  const {translate} = i18n;
  const {cost} = promiseProps;

  const thresholdAmount = Number(
    deliveryEstimates?.freeDeliveryThreshold?.amount,
  );

  if (cost === ScenarioCost.ConditionallyFree && thresholdAmount > 0) {
    const freeDeliveryThresholdAmount =
      formatFreeShippingThreshold(thresholdAmount);

    return {
      ...currentScenario,
      conditionallyFree: translate('conditionallyFree', {
        freeDeliveryThresholdAmount,
      }),
    };
  }

  return currentScenario;
}

function brandLine(
  currentScenario: Scenario,
  {deliveryEstimates, theme}: PipelineProps,
) {
  const {brandedPromise} = deliveryEstimates?.selectedShippingOption || {};
  const logo =
    theme === Theme.Dark ? brandedPromise?.darkLogo : brandedPromise?.lightLogo;

  const showLogo = Boolean(brandedPromise);
  const src = logo?.src || '';
  const altText = logo?.altText || '';
  const brandHandle = brandedPromise?.handle;
  const sdkURL = brandedPromise?.sdkURL;

  if (!brandHandle) {
    return currentScenario;
  }
  return {
    ...currentScenario,
    showLogo,
    logoSrc: src,
    logoAltText: altText,
    brandHandle,
    sdkURL,
  };
}

function conditionalReplaceTitle(
  currentScenario: Scenario,
  {promiseProps}: PipelineProps,
) {
  const {daysToDelivery} = promiseProps;

  if (currentScenario.conditionallyFree && daysToDelivery > 8) {
    return {
      ...currentScenario,
      title: currentScenario.conditionallyFree,
      conditionallyFree: '',
    };
  }

  return currentScenario;
}

function conditionalFasterDelivery(
  currentScenario: Scenario,
  {promiseProps}: PipelineProps,
) {
  const {daysToDelivery, hasFasterOptions} = promiseProps;

  if (
    currentScenario.conditionallyFree &&
    hasFasterOptions &&
    daysToDelivery <= 1
  ) {
    return {
      ...currentScenario,
      fasterOptionsAvailableExtraLineMessage: undefined,
    } satisfies Scenario;
  }

  return currentScenario;
}

function noPromiseForUnqualifiedEstimate(
  currentScenario: Scenario,
  {promiseProps, deliveryEstimates}: PipelineProps,
): Scenario {
  const selectedShippingOption = deliveryEstimates?.selectedShippingOption;
  const {daysToDelivery, cost} = promiseProps;

  let noPromiseReason: string | undefined;
  if (!selectedShippingOption) {
    noPromiseReason = 'no_shipping_option_selected';
  } else if (!selectedShippingOption.reliableDateSource) {
    noPromiseReason = 'no_reliable_date_source';
  } else if (cost === ScenarioCost.Paid && daysToDelivery > 8) {
    noPromiseReason = 'cost_paid_and_delivery_long';
  }

  if (noPromiseReason) {
    return {
      ...getDefaultScenario(),
      hidePromise: true,
      noPromiseReason,
    };
  }

  return currentScenario;
}

export function useShopPromisePipeline() {
  return useMemo(
    () => [
      destinationAddress,
      titleLine,
      destinationLine,
      promiseExpiryLine,
      conditionalFreeLine,
      brandLine,
      conditionalReplaceTitle,
      conditionalFasterDelivery,
      noPromiseForUnqualifiedEstimate,
    ],
    [],
  );
}
